/**
 * Pure rules behind the people pages (Outreachers, Outreacher profile,
 * Creator requests, Lead queue). Kept free of React and env so they can be
 * unit tested directly.
 */

// ── Creator requests ──────────────────────────────────────────────────────

/** Statuses that still need someone's attention. `?status=open` expands to these. */
export const OPEN_REQUEST_STATUSES = [
  'REQUESTED',
  'IN_PROGRESS',
  'AWAITING_RESPONSE',
  'AWAITING_RATE',
] as const;

export const ALL_REQUEST_STATUSES = [
  ...OPEN_REQUEST_STATUSES,
  'ROSTERED',
  'COMPLETED',
  'CANCELLED',
] as const;

/**
 * The `status` URL value → the API's comma-separated `status` param.
 *
 *  - `open` (the default) → every status that isn't finished
 *  - `all` or empty       → no status filter
 *  - a single known code  → that code
 *  - a comma list         → the known codes in it
 *
 * Unknown codes are dropped rather than sent, so a stale link can't make the
 * list silently empty.
 */
export function expandRequestStatusFilter(value: string | null | undefined): string | undefined {
  const v = (value ?? '').trim();
  if (v === '' || v.toLowerCase() === 'all') return undefined;
  if (v.toLowerCase() === 'open') return OPEN_REQUEST_STATUSES.join(',');
  const known = new Set<string>(ALL_REQUEST_STATUSES);
  const codes = v
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => known.has(s));
  return codes.length > 0 ? Array.from(new Set(codes)).join(',') : undefined;
}

/**
 * Title for a finished request's banner. Names who marked it complete only
 * when the data says so; otherwise a neutral "Completed" (never "Closed by
 * requester", which was a guess).
 */
export function completedByLabel(actorName: string | null | undefined): string {
  const name = actorName?.trim();
  return name ? `Marked complete by ${name}` : 'Completed';
}

// ── Lead queue ────────────────────────────────────────────────────────────

/** A folder with open leads whose oldest unclaimed lead is this old is stale. */
export const STALE_FOLDER_DAYS = 7;

/** Same rule as the Home inbox count: open leads, oldest unclaimed ≥ 7 days. */
export function isStaleFolder(
  folder: { available: number; oldestUnclaimedDays: number | null },
  days: number = STALE_FOLDER_DAYS,
): boolean {
  return folder.available > 0 && (folder.oldestUnclaimedDays ?? 0) >= days;
}

/**
 * Bulk delete asks the admin to type how many leads they selected. Accepts
 * surrounding spaces and thousands separators; anything else must match
 * exactly.
 */
export function confirmCountMatches(typed: string, expected: number): boolean {
  if (expected <= 0) return false;
  const cleaned = typed.trim().replace(/[,\s]/g, '');
  if (!/^\d+$/.test(cleaned)) return false;
  return Number(cleaned) === expected;
}

/** Ids that were sent but not acted on (the API returns the ones it did). */
export function skippedIds(sent: readonly string[], done: readonly string[]): string[] {
  const doneSet = new Set(done);
  return sent.filter((id) => !doneSet.has(id));
}

export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

/** "Today", "1 day", "12 days" — for "In queue for" style cells. */
export function formatDayCount(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—';
  if (days <= 0) return 'Today';
  return plural(days, 'day');
}

// ── Outreacher profile ────────────────────────────────────────────────────

export interface OutreacherFunnel {
  logged: number;
  joined: number;
  posted: number;
  /** joined / logged, null when nothing was logged. */
  joinRate: number | null;
  /** posted / joined, null when nobody joined. */
  postRate: number | null;
}

/**
 * Turn the analytics status breakdown into flow counts. Every invite is a
 * log; Posted implies Joined, so joined counts both.
 */
export function outreacherFunnel(
  breakdown: ReadonlyArray<{ status: string; count: number }>,
): OutreacherFunnel {
  let logged = 0;
  let joined = 0;
  let posted = 0;
  for (const row of breakdown) {
    logged += row.count;
    if (row.status === 'VERIFIED' || row.status === 'CONVERTED') joined += row.count;
    if (row.status === 'CONVERTED') posted += row.count;
  }
  return {
    logged,
    joined,
    posted,
    joinRate: logged > 0 ? joined / logged : null,
    postRate: joined > 0 ? posted / joined : null,
  };
}

/** "40%" or "—". */
export function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}
